import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import {
  brandButton,
  brandText,
  glassCard,
  pageBackground,
  textBody,
  textMuted,
} from '../styles/tokens'

interface Props {
  children: ReactNode
  fallback?: (error: Error | null, reset: () => void) => ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

// DefaultFallback is a functional sub-component so we can use the
// useTranslation hook here — the class-component ErrorBoundary itself
// can't. The boundary already isolates render errors in `children`,
// and i18next's common namespace is bundle-loaded at boot, so the
// fallback rendering is safe from the same crash path that triggered
// it.
interface DefaultFallbackProps {
  error: Error | null
  onReload: () => void
}

function DefaultFallback({ error, onReload }: DefaultFallbackProps) {
  const { t } = useTranslation('common')
  return (
    <div className={`${pageBackground} flex min-h-screen items-center justify-center px-6`}>
      <div className={`${glassCard} w-full max-w-md rounded-[24px] p-8 text-center`}>
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-red-400/20 to-amber-400/20 dark:from-red-400/25 dark:to-amber-400/20">
          <AlertTriangle size={26} className="text-red-500 dark:text-red-400" />
        </div>
        <h1 className={`text-2xl font-bold ${brandText}`}>{t('errors.unexpected_title')}</h1>
        <p className={`mt-3 text-sm ${textBody}`}>{t('errors.unexpected_body')}</p>
        {error?.message && (
          <pre
            className={`mt-5 max-w-full overflow-x-auto rounded-xl border border-black/[0.06] bg-surface-light p-3 text-left text-xs ${textMuted} dark:border-bg-line dark:bg-bg-raised`}
          >
            {error.message}
          </pre>
        )}
        <button onClick={onReload} className={`${brandButton} mt-6 px-6 py-2.5 text-sm`}>
          {t('errors.reload_button')}
        </button>
      </div>
    </div>
  )
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo)
    // Forward to Sentry so we see production render-time crashes in the
    // dashboard. Sentry.init no-ops when VITE_SENTRY_DSN is unset, so this
    // is safe to call unconditionally.
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset)
      }
      return <DefaultFallback error={this.state.error} onReload={this.handleReload} />
    }

    return this.props.children
  }
}
